/**
 * Google Drive OAuth Authentication
 *
 * OAuth authentication using expo-auth-session for Google Drive access
 */

import * as AuthSession from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import { reportError } from '@/lib/errors';
import { isWeb } from '@/lib/platform';
import { supabase } from '@/lib/supabase/client';
import {
  getGoogleConfig,
  GOOGLE_DRIVE_SCOPES,
  GOOGLE_DISCOVERY,
} from './config';
import type { GoogleDriveTokens } from './types';

// Complete any pending auth session on web
WebBrowser.maybeCompleteAuthSession();

// Storage key for Google Drive tokens
const GOOGLE_TOKENS_KEY = 'inverio-google-drive-tokens';

/**
 * Web-only: run the Google token exchange/refresh through the
 * `google-token-exchange` edge function, which adds the Web client SECRET
 * server-side (Google's Web client requires it; it can't ship in the bundle).
 * Native (iOS/Android) are public PKCE clients and call Google directly below.
 * The edge function is a pure relay — it never stores the tokens.
 */
async function googleTokenViaEdge(
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke('google-token-exchange', { body });
  if (error) throw new Error(`Token request failed: ${error.message}`);
  return data as Record<string, unknown>;
}

/**
 * Hook for Google OAuth authentication
 *
 * Uses expo-auth-session to handle the OAuth flow with PKCE
 *
 * @returns OAuth request state and functions
 */
export function useGoogleAuth() {
  const config = getGoogleConfig();

  // Get the appropriate client ID based on platform
  const clientId = Platform.select({
    web: config.webClientId,
    ios: config.iosClientId,
    android: config.androidClientId,
    default: config.expoClientId || config.clientId,
  });

  // Create redirect URI using Expo's helper
  // Uses the app scheme defined in app.json
  const redirectUri = AuthSession.makeRedirectUri({
    scheme: 'inverio',
    path: 'oauth/google',
  });

  // Create auth request with PKCE
  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: clientId || '',
      scopes: GOOGLE_DRIVE_SCOPES,
      redirectUri,
      responseType: AuthSession.ResponseType.Code,
      usePKCE: true,
    },
    GOOGLE_DISCOVERY
  );

  return {
    request,
    response,
    promptAsync,
    redirectUri,
    clientId,
    isConfigured: !!clientId,
  };
}

/**
 * Save Google Drive tokens to secure storage
 */
export async function saveGoogleTokens(
  tokens: GoogleDriveTokens
): Promise<void> {
  await SecureStore.setItemAsync(GOOGLE_TOKENS_KEY, JSON.stringify(tokens));
}

/**
 * Get Google Drive tokens from secure storage
 */
export async function getGoogleTokens(): Promise<GoogleDriveTokens | null> {
  const data = await SecureStore.getItemAsync(GOOGLE_TOKENS_KEY);
  if (!data) return null;

  try {
    return JSON.parse(data) as GoogleDriveTokens;
  } catch {
    return null;
  }
}

/**
 * Clear Google Drive tokens from secure storage
 */
export async function clearGoogleTokens(): Promise<void> {
  await SecureStore.deleteItemAsync(GOOGLE_TOKENS_KEY);
}

/**
 * Exchange authorization code for access tokens
 *
 * @param code - Authorization code from OAuth flow
 * @param codeVerifier - PKCE code verifier
 * @param redirectUri - Redirect URI used in the request
 * @returns Google Drive tokens
 */
export async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUri: string
): Promise<GoogleDriveTokens> {
  const config = getGoogleConfig();

  // Get the appropriate client ID based on platform
  const clientId = Platform.select({
    web: config.webClientId,
    ios: config.iosClientId,
    android: config.androidClientId,
    default: config.expoClientId || config.clientId,
  });

  if (!clientId) {
    throw new Error('Google client ID not configured');
  }

  // Web: Google's Web client needs the secret at exchange → go via the edge
  // function. Native: public PKCE client → exchange directly with Google.
  let data: Record<string, any>;
  if (isWeb) {
    data = await googleTokenViaEdge({ mode: 'exchange', clientId, code, codeVerifier, redirectUri });
  } else {
    const response = await fetch(GOOGLE_DISCOVERY.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        code,
        code_verifier: codeVerifier,
        grant_type: 'authorization_code',
        redirect_uri: redirectUri,
      }).toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${errorText}`);
    }
    data = await response.json();
  }

  const tokens: GoogleDriveTokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: data.expires_in
      ? Date.now() + data.expires_in * 1000
      : undefined,
    scope: data.scope,
  };

  // Save tokens for later use
  await saveGoogleTokens(tokens);

  return tokens;
}

/**
 * Refresh an expired access token
 *
 * @param refreshToken - The refresh token
 * @returns New Google Drive tokens
 */
export async function refreshAccessToken(
  refreshToken: string
): Promise<GoogleDriveTokens> {
  const config = getGoogleConfig();

  const clientId = Platform.select({
    web: config.webClientId,
    ios: config.iosClientId,
    android: config.androidClientId,
    default: config.expoClientId || config.clientId,
  });

  if (!clientId) {
    throw new Error('Google client ID not configured');
  }

  // Web goes through the edge function (secret server-side); native direct.
  let data: Record<string, any>;
  if (isWeb) {
    data = await googleTokenViaEdge({ mode: 'refresh', clientId, refreshToken });
  } else {
    const response = await fetch(GOOGLE_DISCOVERY.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }).toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token refresh failed: ${errorText}`);
    }
    data = await response.json();
  }

  const tokens: GoogleDriveTokens = {
    accessToken: data.access_token,
    // Keep the original refresh token if not returned
    refreshToken: data.refresh_token || refreshToken,
    expiresAt: data.expires_in
      ? Date.now() + data.expires_in * 1000
      : undefined,
    scope: data.scope,
  };

  await saveGoogleTokens(tokens);

  return tokens;
}

/**
 * Check if tokens are still valid
 *
 * @param tokens - Google Drive tokens to check
 * @returns true if tokens are valid, false otherwise
 */
export function isTokenValid(tokens: GoogleDriveTokens | null): boolean {
  if (!tokens) return false;
  if (!tokens.accessToken) return false;
  if (!tokens.expiresAt) return true; // No expiry info, assume valid

  // Add 1 minute buffer to avoid edge cases
  return Date.now() < tokens.expiresAt - 60000;
}

/**
 * Get valid tokens, refreshing if necessary
 *
 * @returns Valid Google Drive tokens or null if unavailable
 */
export async function getValidTokens(): Promise<GoogleDriveTokens | null> {
  const tokens = await getGoogleTokens();

  if (!tokens) return null;

  if (isTokenValid(tokens)) {
    return tokens;
  }

  // Token expired, try to refresh
  if (tokens.refreshToken) {
    try {
      return await refreshAccessToken(tokens.refreshToken);
    } catch (error) {
      reportError(error, { context: 'GoogleDriveAuth.getValidAccessToken.refresh', level: 'warning' });
      // Clear invalid tokens
      await clearGoogleTokens();
      return null;
    }
  }

  // No refresh token, clear and return null
  await clearGoogleTokens();
  return null;
}

/**
 * Revoke Google access tokens
 */
export async function revokeGoogleTokens(): Promise<void> {
  const tokens = await getGoogleTokens();

  if (tokens?.accessToken) {
    try {
      // Revoke the token with Google
      await fetch(
        `${GOOGLE_DISCOVERY.revocationEndpoint}?token=${tokens.accessToken}`,
        { method: 'POST' }
      );
    } catch (error) {
      reportError(error, { context: 'GoogleDriveAuth.revokeGoogleTokens', level: 'warning', silent: true });
    }
  }

  // Clear local tokens regardless of revocation result
  await clearGoogleTokens();
}
